import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { errorMessage, formatDateTime, useLoad } from '../members/hooks';
import { filterPii, piiWarning } from '../field/pii';
import { LIMITS, type PartnerPage, type PartnerPageSaved } from './types';
import { toForm, validatePartnerForm, type Form } from './form';
import { logoProblem, uploadLogo } from './uploadLogo';
import { BRAND } from '../../brand';

export function PartnerPageEditor() {
  const { activeOrg } = useSession();
  const orgId = activeOrg?.orgId;
  const loader = useCallback(() => api.get<PartnerPage>('/v1/partner-page', { orgId }), [orgId]);
  const { data, error: loadError, loading } = useLoad(loader);

  const [form, setForm] = useState<Form>(() => toForm(null, activeOrg?.orgName ?? ''));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (data) setForm(toForm(data, activeOrg?.orgName ?? ''));
  }, [data, activeOrg?.orgName]);

  const errors = validatePartnerForm(form);
  const hasErrors = Object.keys(errors).length > 0;
  const textPii = useMemo(() => filterPii(form.text), [form.text]);
  const namePii = useMemo(() => filterPii(form.name), [form.name]);
  const piiKinds = [...new Set([...textPii.kinds, ...namePii.kinds])];

  async function onLogo(file: File | undefined) {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem) {
      setNotice({ tone: 'error', text: problem });
      return;
    }
    setUploading(true);
    setNotice(null);
    try {
      const logo = await uploadLogo(file, orgId);
      setForm((f) => ({ ...f, logoId: logo.logoId, logoPreview: logo.url }));
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    setNotice(null);
    try {
      const saved = await api.request<PartnerPageSaved>('/v1/partner-page', {
        method: 'PUT',
        orgId,
        body: {
          name: form.name.trim() || null,
          text: form.text.trim() || null,
          link: form.link.trim() || null,
          callToAction: form.callToAction.trim() || null,
          logoId: form.logoId,
        },
      });
      setForm(toForm(saved, activeOrg?.orgName ?? ''));
      const warning = saved.warnings?.[0]?.message;
      setNotice(warning ? { tone: 'warning', text: `Publicamos la página. ${warning}` } : { tone: 'success', text: 'Publicamos la página del partner.' });
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  }

  const shownErrors = touched ? errors : {};

  return (
    <section className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">Página del partner</h1>
        <p className="text-sm text-ink-muted">Lo que ve quien escanea un QR tuyo que aún no está activado o está suspendido.</p>
      </div>
      {loadError && <Banner tone="error">{loadError}</Banner>}
      <div aria-live="polite">{notice && <Banner tone={notice.tone}>{notice.text}</Banner>}</div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-line bg-white p-5" noValidate aria-busy={loading || undefined}>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-line bg-mist">
              {form.logoPreview ? <img src={form.logoPreview} alt="Logo actual" className="max-h-full max-w-full" /> : <span className="text-xs text-ink-muted">Sin logo</span>}
            </div>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-ink-muted">Logo (PNG, JPG o SVG, hasta 2 MB)</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                disabled={uploading}
                onChange={(e) => void onLogo(e.target.files?.[0])}
                className="text-sm file:mr-3 file:rounded-md file:border file:border-line file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-semibold"
              />
              {uploading && <span className="ml-2 text-xs text-ink-muted" role="status">Subiendo…</span>}
            </label>
            {form.logoId && (
              <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, logoId: null, logoPreview: null }))}>Quitar logo</Button>
            )}
          </div>

          <Input label="Nombre visible" value={form.name} maxLength={LIMITS.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={shownErrors.name} />

          <div className="flex flex-col gap-1">
            <label htmlFor="partner-text" className="text-xs font-medium text-ink-muted">
              Texto (máx. {LIMITS.text} caracteres, sin HTML)
            </label>
            <textarea
              id="partner-text"
              rows={4}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              aria-invalid={shownErrors.text ? true : undefined}
              aria-describedby="partner-text-count"
              className={`w-full rounded-sm border bg-white px-3 py-2 text-sm text-ink ${shownErrors.text ? 'border-error' : 'border-line'}`}
            />
            <p id="partner-text-count" className={`text-right text-xs tabular-nums ${form.text.length > LIMITS.text ? 'text-error-text' : 'text-ink-muted'}`}>
              {form.text.length}/{LIMITS.text}
            </p>
            {shownErrors.text && <p role="alert" className="text-xs font-medium text-error-text">{shownErrors.text}</p>}
          </div>
          {piiKinds.length > 0 && <Banner tone="warning">{piiWarning(piiKinds)} Usa el enlace para dirigir a tu sitio.</Banner>}

          <Input label="Enlace" type="url" placeholder="https://tuempresa.cl" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} error={shownErrors.link} />
          <Input
            label={`Llamado a escanear para el marco del kit (máx. ${LIMITS.callToAction})`}
            value={form.callToAction}
            maxLength={LIMITS.callToAction}
            onChange={(e) => setForm({ ...form, callToAction: e.target.value })}
            error={shownErrors.callToAction}
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-ink-muted">{data?.updatedAt ? `Última publicación: ${formatDateTime(data.updatedAt)}` : 'Aún no publicada'}</span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => { setForm(toForm(data, activeOrg?.orgName ?? '')); setTouched(false); }}>Descartar</Button>
              <Button type="submit" loading={saving} disabled={uploading}>Guardar y publicar</Button>
            </div>
          </div>
        </form>

        <aside aria-label="Vista previa de la página del partner" className="space-y-2">
          <p className="text-xs font-medium text-ink-muted">Vista previa (lo que ve quien escanea un QR sin activar o suspendido)</p>
          <div className="mx-auto max-w-[20rem] overflow-hidden rounded-xl border border-line bg-chalk shadow-1">
            <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
              {form.logoPreview && <img src={form.logoPreview} alt="" className="h-14 w-auto" />}
              <h2 className="font-display text-lg font-bold text-ink">{namePii.text || activeOrg?.orgName || 'Tu negocio'}</h2>
              {form.text && <p className="whitespace-pre-line text-sm text-ink">{textPii.text}</p>}
              {form.link && !errors.link && (
                <span className="inline-flex rounded-md bg-action px-4 py-2 text-sm font-semibold text-ink">
                  Ir a {form.link.replace(/^https?:\/\//i, '').replace(/\/.*$/, '')}
                </span>
              )}
            </div>
            <p className="border-t border-line py-2 text-center text-xs text-ink-muted">Hecho con {BRAND}</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
